/**
 * Compare Config Utilities - Pure functions for compare panel config updates
 */

import {type Draft, produce} from 'immer';
import _ from 'lodash';

import {
  type ColumnConfig,
  type CompareSettings,
  type MediaBrowserPanelConfig,
} from '../../common';

export type TileControlValue = 'linked' | 'individual' | 'hidden';
export type TileControlType = 'run' | 'step' | 'index' | 'mediaKey';

/**
 * Creates a new column config initialized with current shared values
 * for any individual controls.
 */
export function initializeColumn(
  config: MediaBrowserPanelConfig,
  columnIndex: number
): ColumnConfig {
  const tileControls = config.compareSettings?.tileControls ?? {};
  const column: ColumnConfig =
    config.compareSettings?.columns?.[columnIndex] ?? {};

  // Initialize individual controls with current shared values
  if (tileControls.step === 'individual') {
    column.step = config.stepIndex ?? 0;
  }
  if (tileControls.index === 'individual') {
    column.index = config.mediaIndex ?? 0;
  }
  if (tileControls.mediaKey === 'individual') {
    const sharedKeys = config.mediaKeys ?? [];
    // Store as single string if one key, array if multiple
    if (sharedKeys.length === 0) {
      column.mediaKey = undefined;
    } else {
      column.mediaKey = sharedKeys[0];
    }
  }
  if (tileControls.run === 'individual') {
    const sharedRun = config.compareSettings?.runName;
    if (sharedRun) {
      column.run = sharedRun;
    }
  }

  return column;
}

/**
 * Helper function to ensure compareSettings and columns array exist and are properly sized.
 * Reduces duplication across update functions.
 *
 * @param draft - Immer draft of the config
 * @param columnIndex - Index of the column that needs to exist
 * @returns The column at the specified index (after ensuring it exists)
 */
function ensureColumnExists(
  draft: Draft<MediaBrowserPanelConfig>,
  columnIndex: number
): Draft<ColumnConfig> {
  if (!draft.compareSettings) {
    draft.compareSettings = {};
  }

  if (!draft.compareSettings.columns) {
    draft.compareSettings.columns = [];
  }

  // Initialize missing columns with current shared values for individual controls
  while (draft.compareSettings.columns.length <= columnIndex) {
    const idx = draft.compareSettings.columns.length;
    const newColumn = initializeColumn(draft as MediaBrowserPanelConfig, idx);
    draft.compareSettings.columns.push(newColumn);
  }

  return draft.compareSettings.columns[columnIndex];
}

export function clearColumnValue(
  compareSettings: CompareSettings,
  control: TileControlType
) {
  if (
    !compareSettings.columns ||
    compareSettings.columns.length === 0 ||
    compareSettings.columns.every(col => col[control] === undefined)
  ) {
    return compareSettings;
  }
  return produce(compareSettings, (draft: CompareSettings) => {
    if (draft.columns) {
      draft.columns.forEach((col: ColumnConfig) => {
        delete col[control];
      });
    }
  });
}

/**
 * Updates a tile control mode (linked/individual/hidden).
 *
 * When switching modes (linked ↔ individual):
 * 1. Ensure a shared value exists (if not, promote first column's value to shared)
 * 2. Clear all column-specific values so they all use the shared fallback
 *
 * This ensures all columns start with the same value after mode switch.
 *
 * @param config - Current panel configuration
 * @param control - Which control to update
 * @param value - New value (linked/individual/hidden)
 * @returns Partial configuration with only the changed fields
 */
export function updateTileControl(
  config: MediaBrowserPanelConfig,
  control: TileControlType,
  value: TileControlValue
): Partial<MediaBrowserPanelConfig> {
  const updatedConfig = produce(config, (draft: MediaBrowserPanelConfig) => {
    if (!draft.compareSettings) {
      draft.compareSettings = {};
    }

    if (!draft.compareSettings.tileControls) {
      draft.compareSettings.tileControls = {};
    }
    draft.compareSettings.tileControls[control] = value;

    // Handle transitions between linked/individual/hidden modes
    if (value !== 'hidden') {
      // Always mutate the draft columns array so materialization is persisted
      // when columns was previously undefined (settings-drawer toggle path).
      if (!draft.compareSettings.columns) {
        draft.compareSettings.columns = [];
      }
      const columns = draft.compareSettings.columns;
      const firstColumn = columns.length > 0 ? columns[0] : undefined;

      if (value === 'individual') {
        const columnCount = draft.columnCount ?? 0;
        for (let i = columns.length; i < columnCount; i++) {
          columns.push({});
        }
        // LINKED → INDIVIDUAL: Set each column to the shared value
        const sharedValue = getSharedValue(draft, control);

        columns.forEach((col: ColumnConfig) => {
          // For mediaKey, use first value only (as string, not array)
          if (control === 'mediaKey' && Array.isArray(sharedValue)) {
            col.mediaKey = sharedValue.length > 0 ? sharedValue[0] : undefined;
          } else if (sharedValue !== null && sharedValue !== undefined) {
            col[control as keyof ColumnConfig] = sharedValue as any;
          }
        });
      } else if (value === 'linked') {
        // INDIVIDUAL → LINKED: Set shared value to first column's value

        if (firstColumn) {
          switch (control) {
            case 'step':
              if (firstColumn.step !== undefined) {
                draft.stepIndex = firstColumn.step;
              }
              break;
            case 'index':
              if (firstColumn.index !== undefined) {
                draft.mediaIndex = firstColumn.index;
              }
              break;
            case 'run':
              if (firstColumn.run) {
                if (!draft.compareSettings) {
                  draft.compareSettings = {};
                }
                draft.compareSettings.runName = firstColumn.run;
              }
              break;
            case 'mediaKey':
              if (firstColumn.mediaKey) {
                // Set shared mediaKeys to array with first column's value
                draft.mediaKeys = [firstColumn.mediaKey];
              }
              break;
          }
        }

        // Clear all column-specific values
        columns.forEach((col: ColumnConfig) => {
          delete col[control];
        });
      }
    } else if (value === 'hidden') {
      // When switching to hidden mode, clear all column-specific values
      if (draft.compareSettings.columns) {
        draft.compareSettings.columns.forEach((col: ColumnConfig) => {
          delete col[control];
        });
      }
    }
  });

  // Return only the changed fields as a partial config
  // Include top-level fields that may have been updated (stepIndex, mediaIndex, mediaKeys)
  const result: Partial<MediaBrowserPanelConfig> = {
    compareSettings: updatedConfig.compareSettings,
  };

  // Include top-level fields if they were updated
  if (control === 'step' && updatedConfig.stepIndex !== config.stepIndex) {
    result.stepIndex = updatedConfig.stepIndex;
  }
  if (control === 'index' && updatedConfig.mediaIndex !== config.mediaIndex) {
    result.mediaIndex = updatedConfig.mediaIndex;
  }
  if (
    control === 'mediaKey' &&
    !_.isEqual(updatedConfig.mediaKeys, config.mediaKeys)
  ) {
    result.mediaKeys = updatedConfig.mediaKeys;
  }

  return result;
}

/**
 * Updates the shared run name.
 *
 * When setting the shared run, we clear any column-specific run values to avoid conflicts.
 *
 * @param config - Current panel configuration
 * @param runName - Name of the run to set
 * @returns Partial configuration with only the changed fields (safe for shallow Object.assign)
 */
export function updateSharedRun(
  config: MediaBrowserPanelConfig,
  runName: string
): Partial<MediaBrowserPanelConfig> {
  const updatedConfig = produce(config, (draft: MediaBrowserPanelConfig) => {
    if (!draft.compareSettings) {
      draft.compareSettings = {};
    }

    draft.compareSettings.runName = runName;

    if (draft.compareSettings.columns) {
      draft.compareSettings.columns.forEach((col: ColumnConfig) => {
        delete col.run;
      });
    }
  });

  return {compareSettings: updatedConfig.compareSettings};
}

/**
 * Updates a column's run.
 *
 * When setting a column-specific run in individual mode, we clear the shared run name
 * to avoid conflicts.
 *
 * @param config - Current panel configuration
 * @param columnIndex - Index of the column to update
 * @param runName - Name of the run to set
 * @returns Partial configuration with only the changed fields (safe for shallow Object.assign)
 */
export function updateColumnRun(
  config: MediaBrowserPanelConfig,
  columnIndex: number,
  runName: string
): Partial<MediaBrowserPanelConfig> {
  const updatedConfig = produce(config, (draft: MediaBrowserPanelConfig) => {
    ensureColumnExists(draft, columnIndex).run = runName;

    // TODO: this shouldn't be necessary, move to drawer settings changes
    if (draft.compareSettings) {
      delete draft.compareSettings.runName;
    }
  });

  return {compareSettings: updatedConfig.compareSettings};
}

/**
 * Updates a column's step value.
 *
 * Follows the pattern: initialize all columns with fallback, apply update, then minify.
 *
 * Note: Minification only removes column step values if they match config.stepIndex.
 * If config.stepIndex is undefined, we don't minify - even if the column step is 0 -
 * because the actual fallback chain in getStep includes maxStep, not just 0.
 *
 * @param config - Current panel configuration
 * @param columnIndex - Index of the column to update
 * @param step - Step value to set
 * @returns Updated configuration
 */
export function updateColumnStep(
  config: MediaBrowserPanelConfig,
  columnIndex: number,
  step: number,
  sharedStep: number
): MediaBrowserPanelConfig {
  return produce(config, (draft: MediaBrowserPanelConfig) => {
    // Step 1: Ensure all columns up to columnCount exist
    const columnCount = draft.columnCount ?? 0;
    const maxColumnIndex = Math.max(columnIndex, columnCount - 1);
    for (let i = 0; i <= maxColumnIndex; i++) {
      ensureColumnExists(draft, i);
    }

    // Step 2: Initialize all columns with fallback step value (if they don't have explicit step)
    if (draft.compareSettings?.columns) {
      draft.compareSettings.columns.forEach((col: ColumnConfig) => {
        if (col.step === undefined) {
          col.step = sharedStep;
        }
      });
    }

    // Step 3: Apply the update - set the target column's step
    const column = draft.compareSettings!.columns![columnIndex];
    column.step = step;

    // Step 4: Minify - remove columns whose step matches the minification target
    // Only minify if the step control is set to "linked" (not "individual")
    // When set to "individual", all columns should keep their explicit values
    const stepControl = draft.compareSettings?.tileControls?.step;
    if (stepControl === 'linked' && draft.compareSettings?.columns) {
      draft.compareSettings.columns.forEach((col: ColumnConfig) => {
        if (col.step === sharedStep) {
          delete col.step;
        }
      });
    }
  });
}

/**
 * Updates a column's index value.
 *
 * Follows the pattern: initialize all columns with fallback, apply update, then minify.
 *
 * @param config - Current panel configuration
 * @param columnIndex - Index of the column to update
 * @param index - Index value to set
 * @returns Updated configuration
 */
export function updateColumnIndex(
  config: MediaBrowserPanelConfig,
  columnIndex: number,
  index: number
): MediaBrowserPanelConfig {
  const fallbackIndex = config.mediaIndex ?? 0;

  return produce(config, (draft: MediaBrowserPanelConfig) => {
    // Step 1: Ensure all columns up to columnCount exist
    const columnCount = draft.columnCount ?? 0;
    const maxColumnIndex = Math.max(columnIndex, columnCount - 1);
    for (let i = 0; i <= maxColumnIndex; i++) {
      ensureColumnExists(draft, i);
    }

    // Step 2: Initialize all columns with fallback index value (if they don't have explicit index)
    if (draft.compareSettings?.columns) {
      draft.compareSettings.columns.forEach((col: ColumnConfig) => {
        if (col.index === undefined) {
          col.index = fallbackIndex;
        }
      });
    }

    // Step 3: Apply the update - set the target column's index
    const column = draft.compareSettings!.columns![columnIndex];
    column.index = index;

    // Step 4: Minify - remove columns whose index matches the fallback
    // Only minify if the index control is set to "linked" (not "individual")
    // When set to "individual", all columns should keep their explicit values
    const indexControl = draft.compareSettings?.tileControls?.index;
    if (indexControl === 'linked' && draft.compareSettings?.columns) {
      draft.compareSettings.columns.forEach((col: ColumnConfig) => {
        if (col.index === fallbackIndex) {
          delete col.index;
        }
      });
    }
  });
}

/**
 * Updates a column's media key in individual mode.
 *
 * - All columns are ensured to have a media key (missing ones get the first shared key).
 * - The target column is set to the new key.
 * - If the old key is no longer used by any column, it is removed from config.mediaKeys (order preserved).
 * - If the new key is not in config.mediaKeys, it is appended to the end (order preserved for queries).
 *
 * @param config - Current panel configuration
 * @param columnIndex - Index of the column to update
 * @param mediaKey - Media key to set
 * @returns Partial configuration with only the changed fields
 */
export function updateColumnMediaKey(
  config: MediaBrowserPanelConfig,
  columnIndex: number,
  mediaKey: string
): Partial<MediaBrowserPanelConfig> {
  const updatedConfig = produce(config, (draft: MediaBrowserPanelConfig) => {
    const columnCount = draft.columnCount ?? 0;
    const maxColumnIndex = Math.max(columnIndex, columnCount - 1);
    const currentMediaKeys = draft.mediaKeys ?? [];

    // 1. Ensure all columns exist
    for (let i = 0; i <= maxColumnIndex; i++) {
      ensureColumnExists(draft, i);
    }

    const columns = draft.compareSettings!.columns!;
    const defaultKey = currentMediaKeys[0] ?? mediaKey;

    // 2. Ensure all columns have a media key (e.g. after switching to individual)
    columns.forEach((col: ColumnConfig) => {
      if (col.mediaKey === undefined) {
        col.mediaKey = defaultKey;
      }
    });

    // 3. Capture old key and apply update
    const column = columns[columnIndex];
    const oldKey = column.mediaKey;
    column.mediaKey = mediaKey;
    // reset step and index when media key changes, because
    // the current values may be invalid for the new key
    column.step = undefined;
    column.index = undefined;

    // 4. Build new mediaKeys list: preserve order, remove oldKey if unused, append new if missing
    const keyUsedByOtherColumn = (key: string) =>
      columns.some(
        (col: ColumnConfig, idx: number) =>
          idx !== columnIndex && col.mediaKey === key
      );

    let nextMediaKeys = [...(draft.mediaKeys ?? [])];

    if (oldKey != null && !keyUsedByOtherColumn(oldKey)) {
      nextMediaKeys = nextMediaKeys.filter(k => k !== oldKey);
    }

    if (!nextMediaKeys.includes(mediaKey)) {
      nextMediaKeys.push(mediaKey);
    }

    draft.mediaKeys = nextMediaKeys;
  });

  return {
    mediaKeys: updatedConfig.mediaKeys,
    compareSettings: updatedConfig.compareSettings,
  };
}

/**
 * Updates linked media keys (shared config.mediaKeys).
 *
 * This function updates the shared mediaKeys array that applies to all columns when
 * the mediaKey control is in 'linked' mode. In 'individual' mode, this can also
 * be used to reset columns to use the first key from the new set.
 *
 * @param config - Current panel configuration
 * @param mediaKeys - Media keys to set
 * @param options - Options for updating behavior
 * @param options.mode - 'linked' clears all column-specific values, 'individual' resets columns to first key
 * @param options.availableMediaKeys - Available media keys for fallback when mediaKeys is empty (linked mode only)
 * @returns Updated configuration
 */
export function updateLinkedMediaKeys(
  config: MediaBrowserPanelConfig,
  mediaKeys: string[]
): Partial<MediaBrowserPanelConfig> {
  if (mediaKeys.length === 0) {
    return {};
  }
  const tileControlMode = config.compareSettings?.tileControls?.mediaKey;

  // If control is explicitly linked, use linked mode behavior
  // If control is individual or undefined, use individual mode behavior
  const effectiveMode = tileControlMode === 'linked' ? 'linked' : 'individual';

  const updatedConfig = produce(config, (draft: MediaBrowserPanelConfig) => {
    draft.mediaKeys = mediaKeys;

    // Handle column-specific mediaKey values based on mode
    if (draft.compareSettings?.columns) {
      if (effectiveMode === 'linked') {
        // Clear all column-specific mediaKey values when using linked selector
        draft.compareSettings.columns.forEach((col: ColumnConfig) => {
          delete col.mediaKey;
        });
      } else {
        // In individual mode, reset any columns with explicit mediaKey values to use the first key from the new set
        const firstMediaKey =
          draft.mediaKeys.length > 0 ? draft.mediaKeys[0] : undefined;
        if (firstMediaKey) {
          draft.compareSettings.columns.forEach((col: ColumnConfig) => {
            if (col.mediaKey !== undefined) {
              col.mediaKey = firstMediaKey;
            }
          });
        }
      }
    }
  });

  // Extract only the changed fields for partial update
  const result: Partial<MediaBrowserPanelConfig> = {
    mediaKeys: updatedConfig.mediaKeys,
  };

  // Only include compareSettings if columns were modified
  if (
    updatedConfig.compareSettings?.columns !== config.compareSettings?.columns
  ) {
    result.compareSettings = updatedConfig.compareSettings;
  }

  return result;
}

/**
 * NOTE: columnCount is now stored in config.columnCount (top-level), not compareSettings.
 * To update column count, use: updateConfig({columnCount: value})
 *
 * This function has been removed as it was writing to the wrong location
 * (compareSettings.columnCount instead of config.columnCount).
 */

/**
 * Updates the fan-out variable setting.
 *
 * Fan-out variable determines which variable is used to automatically
 * generate columns (e.g., fanning out by step creates columns for each step).
 *
 * @param config - Current panel configuration
 * @param variable - Variable to fan out by, or null to disable fan-out
 * @returns Updated configuration
 */
export function updateFanOutVariable(
  config: MediaBrowserPanelConfig,
  variable: 'step' | 'index' | null
): MediaBrowserPanelConfig {
  return produce(config, (draft: MediaBrowserPanelConfig) => {
    // Ensure compareSettings exists
    if (!draft.compareSettings) {
      draft.compareSettings = {};
    }

    // Set or clear fan-out variable
    if (variable === null) {
      delete draft.compareSettings.fanOutVariable;
    } else {
      draft.compareSettings.fanOutVariable = variable;
    }
  });
}

/**
 * Gets the shared value for a field (used when control is linked).
 *
 * Shared values are the values that apply to all columns when a control is in 'linked' mode.
 * - step: from config.stepIndex (top-level)
 * - index: from config.mediaIndex (top-level)
 * - run: from config.compareSettings.runName (nested in compareSettings)
 * - mediaKey: from config.mediaKeys (top-level)
 *
 * @param config - Current panel configuration
 * @param field - Field to get shared value for
 * @returns Shared value for the field, or null/0/[] if not set
 */
export function getSharedValue(
  config: MediaBrowserPanelConfig,
  field: 'step' | 'index' | 'run' | 'mediaKey'
): number | string | string[] | null | undefined {
  switch (field) {
    case 'step':
      return config.stepIndex;
    case 'index':
      return config.mediaIndex;
    case 'run':
      return config.compareSettings?.runName ?? null;
    case 'mediaKey':
      return config.mediaKeys ?? [];
  }
}
